/* eslint-disable consistent-return */
import PropTypes from 'prop-types';
import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { UserManager as Manager, WebStorageStateStore } from 'oidc-client';
import { useTranslation } from 'react-i18next';
import Logger from '@medflex/utils/Logger';
import getUserType from '@medflex/utils/getUserType';
import { useLocation, Route, Switch } from 'react-router-dom';
import LoadingView from '../../LoadingView';
import SessionExpiredPage from '../../SessionExpiredPage';
import UserManagerContext from '../UserManagerContext';
import SilentRenew from '../SilentRenew';
import SignOut from '../SignOut';
import useWebViewToken from './useWebViewToken';

const init = ({ ...config }) => new Manager({ ...config });

const MobileUserManagerLegacy = ({
  children,
  basePath,
  silentRedirectUri,
  oidcAuthority,
  clientId,
  postLogoutRedirectUri,
  onSessionLoaded,
  silentRenewDisabled,
  externalExpired,
}) => {
  const location = useLocation();
  const { t } = useTranslation('components');
  const [oidcSession, setOidcSession] = useState(null);
  const [silentlyRenewing, setSilentlyRenewing] = useState(false);
  const [expired, setExpired] = useState(false);
  const [manager] = useState(
    init({
      // reponse_type: 'code' (as opposed to 'id_token' in the implicit flow) means the authorization
      // endpoint (keycloak) does not directly return the token but a short-living code in the url
      // with which the client can request a token
      response_type: 'code',
      scope: 'openid profile email',
      automaticSilentRenew: false,
      monitorSession: false,
      filterProtocolClaims: true,
      loadUserInfo: false,
      silent_redirect_uri: silentRedirectUri || `${window.location.origin}${basePath}/silent-renew`,
      authority: oidcAuthority,
      client_id: clientId,
      post_logout_redirect_uri: postLogoutRedirectUri || `${window.location.origin}${basePath}`,
      clockSkew: 6000,
      accessTokenExpiringNotificationTime: 180,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    }),
  );

  const setSession = useCallback(
    session => {
      const userType = getUserType(session.profile.azp);
      Logger.log(`userType: ${userType}`);
      Logger.log(`expired: ${session.expired}`);
      Logger.log(`expires: ${new Date(session.expires_at * 1000)}`);
      if (!session.expired) setExpired(false);
      const newSession = { ...session, token_type: 'Bearer', userType };
      setOidcSession(newSession);
      onSessionLoaded(newSession);
    },
    [onSessionLoaded],
  );

  const getToken = useWebViewToken({ manager, shouldUseWebViewHandler: true, setSession });

  const signOutRedirect = useCallback(async () => {
    Logger.log(`[MobileUserManagerLegacy::signOutRedirect] - starting user logout`);
    setSilentlyRenewing(false);

    await manager.clearStaleState();
    // This will remove the user from the manager and result in the library not knowing who you are.
    // And it will be called anyway in the signoutRedirect function
    // await manager.removeUser();

    await manager.signoutRedirect({
      id_token_hint: oidcSession ? oidcSession.id_token : null,
      extraQueryParams: {
        client_id: clientId || null,
      },
      post_logout_redirect_uri: postLogoutRedirectUri || null,
    });

    setOidcSession(null);
    Logger.log(`[MobileUserManagerLegacy::signOutRedirect] - finished user logout`);
  }, [clientId, manager, oidcSession, postLogoutRedirectUri]);

  const onAccessTokenExpired = useCallback(
    async e => {
      if (await getToken()) {
        Logger.log(
          `[MobileUserManagerLegacy::onAccessTokenExpired] - reusing existing mobile app token`,
        );
        return;
      }

      Logger.warn(
        `[MobileUserManagerLegacy::onAccessTokenExpired] - access token expired and did not receive valid token from mobile app`,
      );
      setExpired(true);
      const error = {
        ...e,
        message: `[MobileUserManagerLegacy::onAccessTokenExpired] - access token expired. ${e?.message}`,
      };
      Logger.warn(error);
    },
    [getToken],
  );

  const onSilentRenewError = useCallback(
    e => {
      const error = {
        ...e,
        message: `[MobileUserManagerLegacy::onSilentRenewError] - silent renew failed. ${e?.message}`,
      };
      Logger.error(error);
      signOutRedirect();
    },
    [signOutRedirect],
  );

  const onUserSessionChanged = e => {
    Logger.log('[MobileUserManagerLegacy::onUserSessionChanged]', e);
  };

  const onAccessTokenExpiring = useCallback(() => {
    getToken();
    Logger.log('[MobileUserManagerLegacy::onAccessTokenExpiring]');
  }, [getToken]);

  const onUserLoaded = useCallback(
    user => {
      // "user" is a Keycloak Session Object, containing an id_token, access_token, profile, ...
      Logger.info(`[MobileUserManagerLegacy::onUserLoaded] ${user?.profile?.userId}`, user);
      setSession(user);
    },
    [setSession],
  );

  useEffect(() => {
    if (externalExpired) {
      Logger.warn(
        `[MobileUserManagerLegacy] -> websocket reported expired token; trigger WebView handler`,
      );
      getToken();
    }
  }, [externalExpired, getToken]);

  useEffect(() => {
    if (manager && oidcSession) {
      if (silentRenewDisabled && silentlyRenewing) {
        Logger.log('[MobileUserManagerLegacy] - stop silent renew');
        manager.stopSilentRenew();
        setSilentlyRenewing(false);
      }
      if (!silentRenewDisabled && !silentlyRenewing) {
        Logger.log('[MobileUserManagerLegacy] - start silent renew');
        manager.startSilentRenew();
        setSilentlyRenewing(true);
      }
    }
  }, [manager, silentRenewDisabled, oidcSession, silentlyRenewing]);

  useEffect(() => {
    if (manager) {
      manager.events.addUserLoaded(onUserLoaded);
      manager.events.addAccessTokenExpiring(onAccessTokenExpiring);
      manager.events.addAccessTokenExpired(onAccessTokenExpired);
      manager.events.addSilentRenewError(onSilentRenewError);
      manager.events.addUserSessionChanged(onUserSessionChanged);

      return () => {
        manager.events.removeUserLoaded(onUserLoaded);
        manager.events.removeAccessTokenExpiring(onAccessTokenExpiring);
        manager.events.removeAccessTokenExpired(onAccessTokenExpired);
        manager.events.removeSilentRenewError(onSilentRenewError);
        manager.events.removeUserSessionChanged(onUserSessionChanged);
      };
    }
  }, [manager, onAccessTokenExpired, onAccessTokenExpiring, onSilentRenewError, onUserLoaded]);

  const managerValues = useMemo(() => ({ manager, signOutRedirect }), [manager, signOutRedirect]);

  return (
    <UserManagerContext.Provider value={managerValues}>
      <Switch>
        <Route exact path="*/silent-renew" component={SilentRenew} />
        <Route exact path="/abmelden" component={SignOut} />
        <Route>
          {() => {
            if (expired) {
              return (
                <SessionExpiredPage
                  title={t('UserManager.SessionExpiredPage.title')}
                  text={t('UserManager.SessionExpiredPage.text')}
                  buttonLabel={t('UserManager.SessionExpiredPage.button_label')}
                  onClick={() => {
                    return getToken();
                  }}
                />
              );
            }
            if (oidcSession && !oidcSession.expired) return children;
            return (
              <LoadingView
                title={t('UserManager.LoadingView.title')}
                message={t('UserManager.LoadingView.message')}
              />
            );
          }}
        </Route>
      </Switch>
    </UserManagerContext.Provider>
  );
};

MobileUserManagerLegacy.propTypes = {
  children: PropTypes.node,
  basePath: PropTypes.string,
  oidcAuthority: PropTypes.string,
  clientId: PropTypes.oneOf(['medflex-web-physician', 'medflex-web-patient', 'medflex-web-staff']),
  postLogoutRedirectUri: PropTypes.string,
  onSessionLoaded: PropTypes.func,
  silentRenewDisabled: PropTypes.bool,
  externalExpired: PropTypes.bool,
  silentRedirectUri: PropTypes.string,
};

MobileUserManagerLegacy.defaultProps = {
  onSessionLoaded: () => {},
  basePath: '',
  children: null,
  clientId: null,
  oidcAuthority: null,
  postLogoutRedirectUri: null,
  silentRenewDisabled: null,
  externalExpired: null,
  silentRedirectUri: null,
};

export default MobileUserManagerLegacy;
